// Presale state management slice

import { create } from 'zustand';
import { PresalePhase, PresaleStats, PresaleState } from '../types/presale';
import { PRESALE_CONFIG } from '../utils/constants';

export interface PresaleSlice extends PresaleState {
  // Actions
  setPhases: (phases: PresalePhase[]) => void;
  setCurrentPhase: (phase: number) => void;
  setStats: (stats: PresaleStats) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  updateStats: () => void;
  initializePresale: () => void;
}

// Create presale slice
export const presaleSlice = (set: any, get: any): PresaleSlice => ({
  // Initial state
  phases: PRESALE_CONFIG.PHASES.map((phase, index) => ({
    ...phase,
    allocation: phase.allocation.toString(),
    price: phase.price.toString(),
    isActive: index === 0,
  })),
  currentPhase: 0,
  stats: {
    tokensSold: 1250000,
    tokensRemaining: 1750000,
    percentageSold: 41.7,
    currentPrice: 'Rp 26,000',
    totalAllocation: 10000000,
  },
  isLoading: false,
  error: null,

  // Set phases
  setPhases: (phases: PresalePhase[]) => {
    set({ phases });
  },

  // Set current phase
  setCurrentPhase: (phase: number) => {
    set({ currentPhase: phase });
  },

  // Set stats
  setStats: (stats: PresaleStats) => {
    set({ stats });
  },

  // Set loading state
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Set error
  setError: (error: string | null) => {
    set({ error });
  },

  // Update stats (simulate dynamic token sales)
  updateStats: () => {
    const { stats } = get();
    
    // Simulate dynamic token sales
    const totalTokensSold = stats.tokensSold + Math.floor(Math.random() * 100) + 50;
    const tokensRemaining = Math.max(0, stats.totalAllocation - totalTokensSold);
    const percentageSold = Math.min(100, (totalTokensSold / stats.totalAllocation) * 100);
    
    set({
      stats: {
        ...stats,
        tokensSold: totalTokensSold,
        tokensRemaining,
        percentageSold,
      }
    });
  },

  // Initialize presale
  initializePresale: () => {
    const { setPhases, setStats } = get();
    
    // Set initial phases
    setPhases(PRESALE_CONFIG.PHASES.map((phase, index) => ({
      ...phase,
      allocation: phase.allocation.toString(),
      price: phase.price.toString(),
      isActive: index === 0,
    })));
    
    // Set initial stats
    setStats({
      tokensSold: 1250000,
      tokensRemaining: 1750000,
      percentageSold: 41.7,
      currentPrice: 'Rp 26,000',
      totalAllocation: 10000000,
    });
  },
});

// Export a hook to use only the presale slice
export const usePresaleStore = create<PresaleSlice>((set, get) => ({
  ...presaleSlice(set, get),
}));